import type { PlanDto } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Archive, CreditCard, PencilLine } from 'lucide-react';
import { usePlansContent } from '../../content/sections/plans.js';
import { formatCents } from '../../lib/currency.js';

/* Planos públicos: CRUD pelo painel (ADR-0419). `canManage` (super_admin/billing_ops)
   habilita editar/arquivar; demais veem só-leitura. Tabela sobre o primitive `Table`
   de @planuze/ui (ADR-0700); coluna de preço com tabular-nums. */
interface Props {
  readonly plans: readonly PlanDto[];
  readonly canManage: boolean;
  readonly onEdit: (plan: PlanDto) => void;
  readonly onArchive: (plan: PlanDto) => void;
  /** Há busca ativa — diferencia "sem resultados" de "sem planos" (ADR-0702). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
  readonly onToggleAll: (checked: boolean) => void;
}

export function PublicPlansTable({
  plans,
  canManage,
  onEdit,
  onArchive,
  hasFilters,
  onClearFilters,
  selectedIds,
  onToggle,
  onToggleAll,
}: Props) {
  const copy = usePlansContent();
  if (plans.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<CreditCard className="size-6" />}
        title={copy.publicTable.emptyFilteredTitle}
        description={copy.publicTable.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.common.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState icon={<CreditCard className="size-6" />} title={copy.publicTable.emptyTitle} />
    );
  }
  const eligible = plans.filter((plan) => plan.status === 'active');
  const allSelected = eligible.length > 0 && eligible.every((plan) => selectedIds.has(plan.id));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {canManage && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={eligible.length === 0}
                onCheckedChange={(checked) => onToggleAll(checked === true)}
                aria-label={copy.publicSection.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.publicTable.columnPlan}</TableHead>
          <TableHead>{copy.publicTable.columnLookupKey}</TableHead>
          <TableHead>{copy.publicTable.columnTier}</TableHead>
          <TableHead>{copy.publicTable.columnVisibility}</TableHead>
          <TableHead>{copy.publicTable.columnPrice}</TableHead>
          <TableHead>{copy.publicTable.columnInterval}</TableHead>
          <TableHead>{copy.publicTable.columnCapabilities}</TableHead>
          {canManage && (
            <TableHead className="text-right">{copy.publicTable.columnActions}</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.map((plan) => (
          <TableRow key={plan.id}>
            {canManage && (
              <TableCell>
                <Checkbox
                  checked={selectedIds.has(plan.id)}
                  disabled={plan.status !== 'active'}
                  onCheckedChange={() => onToggle(plan.id)}
                  aria-label={copy.publicSection.selectRowAriaLabel(plan.displayName)}
                />
              </TableCell>
            )}
            <TableCell className="text-foreground">
              <span className="inline-flex items-center gap-2">
                {plan.displayName}
                {plan.status === 'archived' && (
                  <StatusBadge variant="neutral">{copy.publicTable.archivedBadge}</StatusBadge>
                )}
              </span>
            </TableCell>
            <TableCell>
              <code className="text-xs text-muted-foreground">{plan.lookupKey}</code>
            </TableCell>
            <TableCell>
              <StatusBadge variant="info">{plan.tier}</StatusBadge>
            </TableCell>
            <TableCell>
              <StatusBadge variant={plan.isPublic ? 'success' : 'neutral'}>
                {plan.isPublic ? copy.publicTable.visible : copy.publicTable.hidden}
              </StatusBadge>
            </TableCell>
            <TableCell numeric className="text-muted-foreground">
              {formatCents(plan.priceUsdCents, plan.currency)}
            </TableCell>
            <TableCell className="text-muted-foreground">{plan.interval}</TableCell>
            <TableCell className="text-muted-foreground">
              {copy.publicTable.capabilitySummary(Object.keys(plan.capabilities).length)}
            </TableCell>
            {canManage && (
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={copy.publicTable.editAriaLabel(plan.displayName)}
                    onClick={() => onEdit(plan)}
                  >
                    <PencilLine className="size-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={copy.publicTable.archiveAriaLabel(plan.displayName)}
                    onClick={() => onArchive(plan)}
                  >
                    <Archive className="size-4" />
                  </Button>
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
